import { Button, Icon, Text, Tooltip } from "@ttc3k/trekker";
import { NoteManyQuery } from "gql/generated";
import { Box, Flex } from "styled-system/jsx";
import { CheckCircle, CheckCircleSolid, EditPencil } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { isToday } from "date-fns";
import { DATE_PATTERN_TIME, formatDate, parseDate } from "shared/utils";
import { useState } from "react";
import { motion } from "framer-motion";
import { getNameFromUser } from "shared/utils/getNameFromUser";

type StaticNoteContentProps = {
  note: NoteManyQuery["noteMany"][0];
  viewOnly?: boolean;
  onEditClick?: () => void;
  onResolveNoteClick?: () => void;
};

/**
 * Displays note information & provides tools to edit or resolve a note
 */
export const StaticNoteContent = ({
  note,
  viewOnly = false,
  onEditClick,
  onResolveNoteClick,
}: StaticNoteContentProps) => {
  const [isSelected, setIsSelected] = useState(false);
  const { t } = useTranslation();
  const isNoteResolved = !!note.resolvedBy;

  const updatedDate = parseDate(note.updatedDateISO);
  const isDateToday = isToday(updatedDate);

  const handleResolveNoteClick = () => {
    setIsSelected(true);
    setTimeout(() => {
      onResolveNoteClick?.();
    }, 1000);
  };
  return (
    <>
      <Box h={"140px"} overflowY={"auto"}>
        <Text color={"text.dark"}>{note.message}</Text>
      </Box>

      <Flex
        gap={"250"}
        justifyContent={"space-between"}
        alignItems={"flex-end"}
      >
        <Box>
          <Text
            visual={"smallMedium"}
            color={isNoteResolved ? "text.success.dark" : "text.highlight.dark"}
          >
            {getNameFromUser(note.author)}
          </Text>
          <Text
            visual={"smallMedium"}
            color={isNoteResolved ? "text.success.dark" : "text.highlight.dark"}
          >
            {isDateToday && formatDate(updatedDate, DATE_PATTERN_TIME)}
            {!isDateToday && formatDate(updatedDate)}
          </Text>
        </Box>
        <Flex gap={"100"}>
          {!viewOnly && onEditClick && (
            <Tooltip
              content={t("core:NOTES.ICONS.EDIT")}
              positioning={{ placement: "top" }}
            >
              <Button
                visual="unstyled"
                onClick={onEditClick}
                borderRadius="full"
              >
                <Icon
                  Element={EditPencil}
                  color={"icon.highlight.mid"}
                  size={"lg"}
                />
              </Button>
            </Tooltip>
          )}
          {!viewOnly && onResolveNoteClick && (
            <Tooltip
              content={t("core:NOTES.ICONS.RESOLVE")}
              positioning={{ placement: "top" }}
            >
              <Button
                visual="unstyled"
                onClick={handleResolveNoteClick}
                borderRadius="full"
              >
                {isSelected ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{
                      type: "spring",
                      bounce: true,
                      stiffness: 500,
                      damping: 20,
                      duration: 0.2,
                    }}
                  >
                    <Icon
                      Element={CheckCircleSolid}
                      color={"icon.success.mid"}
                      size={"lg"}
                    />
                  </motion.div>
                ) : (
                  <Icon
                    Element={CheckCircle}
                    color={"icon.highlight.mid"}
                    size={"lg"}
                  />
                )}
              </Button>
            </Tooltip>
          )}
          {/*
          I modified the layout a little here to account for longer names and dates
          - It was breaking the text weirdly and messing up the alignment
          */}
          {isNoteResolved && (
            <Flex
              flexDir="row"
              justifyContent={"flex-end"}
              alignItems={"center"}
              gap="150"
            >
              <Text visual={"smallRegular"} color={"text.success.dark"}>
                {t("core:NOTES.RESOLVED_BY")}
                <br />
                <Text as={"span"} visual={"smallSemiBold"}>
                  {getNameFromUser(note.resolvedBy)}
                </Text>
              </Text>
              <Icon
                Element={CheckCircleSolid}
                size={"icon.lg"}
                color={"icon.success.mid"}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
};
